"use client";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "next/link";
import { FunnelButton } from "../../FunnelButton";
import { CallDetailsCard } from "./CallDetailsCard";
import { IndustryExploreLink } from "./IndustryExploreLink";
import { QueueHeadline } from "./QueueHeadline";
import { getUrls } from "@portal/app/_common/lib/urls";
import type { VocationIndustry } from "@portal/app/_common/types/vocationIndustry";

interface FinancialAidScreenProps {
  title: string;
  body: string;
  isBodyMarkdown: boolean;
  industry?: VocationIndustry;
  /** When set, replaces the default "Start your FAFSA" CTA. */
  primaryCtaOverride?: { text: string; href: string };
}

export const FinancialAidScreen = ({
  title,
  body,
  isBodyMarkdown,
  industry,
  primaryCtaOverride,
}: FinancialAidScreenProps) => {
  const ctaText = primaryCtaOverride?.text ?? "Start your FAFSA";
  const ctaHref = primaryCtaOverride?.href ?? getUrls().FederalFinancialAid();
  const isExternal = ctaHref.startsWith("http");

  return (
    <>
      <QueueHeadline title={title} body={body} isMarkdown={isBodyMarkdown} />
      <CallDetailsCard />
      <Box sx={{ width: "100%", textAlign: "left", mt: 1 }}>
        <Typography
          variant="overline"
          color="primary.main"
          sx={{ letterSpacing: 1, fontWeight: 700, display: "block", mb: 1 }}
        >
          What&rsquo;s next?
        </Typography>
        <Typography variant="body1" data-testid="success-fafsa-prompt">
          Start your <strong>FAFSA</strong> now to find out what aid you qualify for before the call.
        </Typography>
      </Box>
      <Box sx={{ mt: 1, width: "100%" }}>
        {isExternal ? (
          <FunnelButton
            component="a"
            href={ctaHref}
            // FunnelButton's prop type doesn't surface anchor attributes; the
            // spread is the established way to forward target/rel through MUI.
            {...{ target: "_blank", rel: "noopener noreferrer" }}
            data-testid="success-cta"
          >
            {ctaText}
          </FunnelButton>
        ) : (
          <Link href={ctaHref} passHref legacyBehavior>
            <FunnelButton component="a" data-testid="success-cta">
              {ctaText}
            </FunnelButton>
          </Link>
        )}
      </Box>
      <IndustryExploreLink industry={industry} />
    </>
  );
};
